import { useState } from "react";
import { Alert, Button, Flex, Typography, Upload, UploadFile } from "antd";
import {
  useCreateJoyTgtUploadMutation,
  useGetJoyTgtUploadQuery,
} from "../../../api/admin/joyTgtApi";

export default function JoyTgtMappingPage() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [jobId, setJobId] = useState<string>();
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [createUpload] = useCreateJoyTgtUploadMutation();
  const { data, error } = useGetJoyTgtUploadQuery(jobId, {
    pollingInterval: jobId ? 3000 : 0,
  });
  const processing = data?.Status === "applying"
    || data?.Status === "uploaded";

  async function upload() {
    setUploading(true);
    setFailure(undefined);
    try {
      const file = files[0].originFileObj;
      if (!file) {
        throw new Error("Select a CSV file.");
      }
      const result = await createUpload({ filename: file.name }).unwrap();
      const response = await fetch(result.uploadUrl, {
        method: "PUT",
        body: file,
      });
      if (!response.ok) {
        throw new Error("File upload failed. Please try again.");
      }
      setJobId(result.jobId);
      setFiles([]);
    } catch (problem) {
      const message = problem instanceof Error
        ? problem.message
        : "Upload failed. Please try again.";
      setFailure(message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Flex vertical gap={16}>
      <Typography.Title level={2}>Joy-TGT</Typography.Title>
      <Alert
        type="info"
        message="Replacement mapping"
        description={(
          <>
            <p>
              Upload the complete CSV with JOY SKU and TGT SKU columns.
              Omitted or incomplete pairs are removed.
            </p>
            <p>
              Replacement runs automatically after a confirmed critical
              purchase failure, once per SIM/redemption row. Unmapped
              products fail normally.
            </p>
            <p>
              Uncertain purchase outcomes stop for support review.
              Failures after purchase acceptance do not trigger replacement.
            </p>
            <p>
              View results in Redemption Details → Product Details →
              Order Processing → Joy-TGT. The original SIMPLY SKU is retained.
            </p>
          </>
        )}
      />
      <Upload.Dragger
        accept=".csv"
        maxCount={1}
        fileList={files}
        beforeUpload={() => false}
        onChange={({ fileList }) => setFiles(fileList)}
      >
        Select or drop the complete mapping CSV (maximum 2 MB).
      </Upload.Dragger>
      <Button
        type="primary"
        onClick={upload}
        loading={uploading}
        disabled={files.length !== 1 || processing}
      >
        Upload
      </Button>
      {(failure || error) && (
        <Alert
          type="error"
          message={failure || "Unable to read upload status. Please refresh."}
        />
      )}
      {data?.Status && (
        <Alert
          type={data.Status === "failed" ? "error" : "info"}
          message={`Upload status: ${data.Status}`}
          description={data.Details?.Error || (
            data.Details?.Imported !== undefined
              ? `${data.Details.Imported} pairs imported; `
                + `${data.Details.Skipped || 0} incomplete rows skipped.`
              : undefined
          )}
        />
      )}
    </Flex>
  );
}
